import React, { useEffect, useState } from 'react';
import { Box, Loader, Paper, Text, Flex } from '@mantine/core';
import { PlannerWithDetails } from '@/common/types/databaseTypes';
import { TimeBlockBadges } from '@/common/components/badges';
import DueDateBadge from '@/features/todo/components/DueDateBadge';
import { useTodo } from '@/features/todo/services/useTodoQueries';
import { usePlannerStore } from '@/stores/plannerStore';
import { usePlannersByTodo } from '@/features/planner/services/usePlannerQueries';

interface GetTodoResultProps {
    todoId: string;
    plannerId: string; // 특정 플래너 ID - 필수 파라미터
}

const GetTodoResult: React.FC<GetTodoResultProps> = ({ todoId, plannerId }) => {
    // 플래너 스토어에서 날짜 설정 액션 가져오기
    const {
        dateActions: { setSelectedDate },
    } = usePlannerStore();

    // useTodo 훅을 사용해 할 일 정보 가져오기
    const {
        data: todo,
        isLoading: todoLoading,
        error: todoError,
    } = useTodo(todoId);

    // usePlannersByTodo 훅을 사용해 플래너 정보 가져오기
    const {
        data: planners,
        isLoading: plannersLoading,
        error: plannersError,
    } = usePlannersByTodo(todoId);
    console.log('[GetTodoResult] planners:', planners);

    // 특정 플래너 ID가 있는 경우 해당 플래너만 필터링
    const [selectedPlanner, setSelectedPlanner] =
        useState<PlannerWithDetails | null>(null);

    useEffect(() => {
        if (planners && planners.length > 0) {
            // 특정 플래너 ID로 해당 플래너 찾기
            const found = planners.find(p => p.id === plannerId);
            if (found) {
                setSelectedPlanner(found);
                console.log('[GetTodoResult] 특정 플래너 찾음:', found);
            } else {
                // 특정 플래너가 없는 경우 가장 최근 플래너 사용
                setSelectedPlanner(planners[planners.length - 1]);
                console.log(
                    '[GetTodoResult] 특정 플래너 없음, 최근 플래너 사용:',
                    planners[planners.length - 1]
                );
            }
        }
    }, [planners, plannerId]);

    // 로딩 및 에러 상태 통합
    const isLoading = todoLoading || plannersLoading;
    const error = todoError || plannersError;

    // 데이터가 로드되면 blocks 값 확인
    useEffect(() => {
        if (selectedPlanner?.blocks) {
            console.log(
                '[GetTodoResult] selectedPlanner.blocks 값 확인:',
                selectedPlanner.blocks
            );
        }

        if (todo?.planners?.[0]?.blocks) {
            console.log(
                '[GetTodoResult] todo.planners[0].blocks 값 확인:',
                todo.planners[0].blocks
            );
        }
    }, [todo, selectedPlanner]);

    if (isLoading) {
        return (
            <Box p="md" style={{ textAlign: 'center' }}>
                <Loader size="sm" />
                <Text size="xs" color="dimmed" mt={8}>
                    할 일 정보 로딩 중...
                </Text>
            </Box>
        );
    }

    // 에러 처리
    if (error instanceof Error || !todo) {
        return (
            <Box p="md" style={{ textAlign: 'center', color: 'red' }}>
                <Text size="sm">할 일 정보를 불러올 수 없습니다</Text>
                <Text size="xs" color="dimmed" mt={4}>
                    ID: {todoId}
                </Text>
            </Box>
        );
    }

    return (
        <Paper
            p="sm"
            radius="md"
            shadow="none"
            style={{
                width: '445px',
                overflow: 'hidden',
                background: '#FFFFFF',
                minHeight: '92px',
                borderRadius: '8px',
                border: '1px solid #DEE2E6',
            }}
        >
            {/* 내용 부분 - PlannerItem 스타일 적용 */}
            <Box p={0}>
                <Paper
                    shadow="none"
                    w="100%"
                    style={{
                        position: 'relative',
                    }}
                >
                    <Flex gap="sm" align="flex-start">
                        <Box w="100%">
                            <Flex gap={0} align="center" w="100%">
                                <Flex
                                    align="center"
                                    gap={8}
                                    style={{
                                        flex: 1,
                                        overflow: 'hidden',
                                    }}
                                >
                                    {/* 마감일 배지 */}
                                    {todo.due_date && (
                                        <DueDateBadge
                                            todoId={todo.id}
                                            dueDate={todo.due_date}
                                            isButton={false}
                                        />
                                    )}

                                    {/* 할 일 제목 */}
                                    <Text
                                        fw={400}
                                        size="sm"
                                        style={{ color: '#343A40' }}
                                    >
                                        {todo.title}
                                    </Text>
                                </Flex>
                            </Flex>

                            {/* 일정 및 카테고리 정보 */}
                            {/* <Box mb="xs"> */}
                            {/* 목표 정보 - 목표가 없을 때도 동일한 공간 유지 */}
                            <Box mt="2px" mb="4px" h="18px"> {/* 고정 높이로 공간 확보 */}
                                {todo.goal_id && (
                                    <Text size="xs" c="dimmed">
                                        {todo.goal?.title || ''}
                                    </Text>
                                )}
                            </Box>

                            {/* 시간 블록 배지 */}
                            {selectedPlanner?.blocks && (
                                <TimeBlockBadges
                                    blocks={selectedPlanner.blocks}
                                    isHovered={false}
                                    colorName={
                                        todo?.category &&
                                        typeof todo.category === 'object' &&
                                        'color_name' in todo.category
                                            ? todo.category.color_name
                                            : 'blue'
                                    }
                                />
                            )}
                            {/* </Box> */}
                        </Box>
                    </Flex>
                </Paper>
            </Box>
        </Paper>
    );
};

export default GetTodoResult;
